import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useQueryClient } from 'react-query';
import { pullDelta, getLastSync, isInitialSyncComplete, forgetUnviewable, SyncStorageFullError } from './syncClient';
import { requestBackgroundDrainSync } from '../serviceWorkerRegistration';
import { drainQueue } from './pushQueue';
import { pendingCount, conflictOps, errorCount, reviewOps as loadReviewOps } from './opQueue';
import { resolveKeepMine, resolveKeepServer, retryOp, discardOp } from './resolve';
import { OpRow } from './db';
import { emitAtRiskTelemetry } from './atRiskTelemetry';
import {
  isEffectivelyOnline,
  subscribeConnectivity,
  reportConnectivitySuccess,
  reportFromError,
} from './connectivity';

export type ReviewAction = 'keepMine' | 'keepServer' | 'retry' | 'discard';

interface OfflineState {
  isOnline: boolean;
  syncing: boolean;
  lastSyncAt?: string;
  /** queued offline writes not yet acked by the server */
  unsyncedCount: number;
  /** queued writes the server rejected as conflicts (need review) */
  conflictCount: number;
  /** queued writes the server rejected with an error (failed to save) */
  failedCount: number;
  /** parked ops (conflict + error) needing manual resolution */
  reviewOps: OpRow[];
  /** Durability advisory (NOT a write gate as of 2026-06-09): null = still
   *  checking; false = storage is NOT persisted (the browser MAY evict offline
   *  data under disk pressure → drives a "sync soon" warning, but writes still
   *  go through); true = persistent storage granted (safe to capture offline). */
  persisted: boolean | null;
  /** B6: the last pull hit an IndexedDB quota error — the replica is partial and
   *  offline writes are blocked until space is freed. */
  storageFull: boolean;
  /** B9: false while the FIRST full pull is still downloading (so the UI can
   *  show a "downloading clinic data…" state). Defaults true (non-first-run). */
  initialSyncDone: boolean;
  resolveReview: (opId: string, action: ReviewAction) => Promise<void>;
  refresh: () => void;
}

const Ctx = createContext<OfflineState>({
  isOnline: true,
  syncing: false,
  unsyncedCount: 0,
  conflictCount: 0,
  failedCount: 0,
  reviewOps: [],
  persisted: null,
  storageFull: false,
  initialSyncDone: true,
  resolveReview: async () => {},
  refresh: () => {},
});

export const useOffline = (): OfflineState => useContext(Ctx);

/**
 * Run `work` while holding a cross-tab Web Lock so two open tabs don't sync
 * simultaneously. Falls back to running directly where Web Locks are absent
 * (older browsers, jsdom tests). Exported for the workstation lock, which pushes
 * the queue before a PIN swap and must never push an op at the same time as this.
 */
export async function withSyncLock(work: () => Promise<void>): Promise<void> {
  const locks: any = (navigator as any).locks;
  if (locks?.request) {
    // B5: NO `ifAvailable` — a 2nd tab QUEUES for the lock and runs when the
    // first releases, instead of silently skipping its pull+drain. work()
    // always settles (drainQueue swallows network errors; pullDelta throws are
    // caught by sync()), so the lock auto-releases — no deadlock, FIFO-fair.
    await locks.request('stabletrack-sync', work);
    return;
  }
  await work();
}

export const OfflineProvider: React.FC<{
  clinicId?: string;
  /** Current user's uid — lets the replica detect a same-clinic user switch so a
   * different vet never inherits/mis-authors the B3-preserved op-queue. */
  userId?: string;
  /** The view permissions the person holds, once known (fix 87): a revoked one empties its tables on the next sync. */
  viewable?: string[];
  children: React.ReactNode;
}> = ({ clinicId, userId, viewable, children }) => {
  const queryClient = useQueryClient();
  // A string, so a new array with the same slugs does not re-create sync() and re-run the mount effect.
  const viewableKey = viewable ? [...viewable].sort().join(',') : undefined;
  // EFFECTIVE onlineness: navigator.onLine AND observed reachability. A bare
  // navigator.onLine is stale-true on machines with virtual adapters
  // (Docker/WSL/Hyper-V/VPN), which made the app act online with the wifi off.
  const [isOnline, setOnline] = useState<boolean>(isEffectivelyOnline());
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | undefined>();
  const [unsyncedCount, setUnsyncedCount] = useState(0);
  const [conflictCount, setConflictCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [reviewList, setReviewList] = useState<OpRow[]>([]);
  // A4 durability gate. null = unknown/checking.
  const [persisted, setPersisted] = useState<boolean | null>(null);
  // B6: set when a pull hits an IndexedDB quota error (partial replica).
  const [storageFull, setStorageFull] = useState(false);
  // B9: false while the first full pull is still downloading.
  const [initialSyncDone, setInitialSyncDone] = useState(true);
  // B-2: in-flight guard so concurrent triggers don't double-sync within a tab.
  const syncingRef = useRef(false);
  // B5: cross-tab channel — a reconciling drain in one tab tells the others to
  // refresh (NOT re-sync). Guarded for jsdom / old browsers without it.
  const channel = useMemo(
    () => (typeof BroadcastChannel === 'function' ? new BroadcastChannel('stabletrack-offline') : null),
    [],
  );

  // Request + record persistent-storage status. This is ADVISORY only — offline
  // writes are NOT gated on it (executeWrite no longer blocks); when false the UI
  // surfaces a "sync soon" warning. Requested here on mount (best-effort).
  const checkPersisted = useCallback(async () => {
    const storage = (navigator as any).storage;
    if (!storage || typeof storage.persisted !== 'function') {
      setPersisted(false);
      return;
    }
    try {
      let granted = await storage.persisted();
      if (!granted && typeof storage.persist === 'function') {
        granted = await storage.persist();
      }
      setPersisted(granted);
    } catch {
      setPersisted(false);
    }
  }, []);

  const refreshCounts = useCallback(async () => {
    try {
      setUnsyncedCount(await pendingCount());
      setConflictCount((await conflictOps()).length);
      setFailedCount(await errorCount());
      setReviewList(await loadReviewOps());
    } catch {
      /* ignore */
    }
  }, []);

  const sync = useCallback(async () => {
    if (!clinicId || !navigator.onLine) {
      void refreshCounts();
      return;
    }
    // B-2: intra-tab de-dup. B5 dropped the Web Lock's `ifAvailable`, so a burst
    // of triggers (e.g. `online` + `visibilitychange` on the same reconnect) would
    // otherwise queue back-to-back full pulls. The cross-tab lock still serializes
    // ACROSS tabs; this guards redundant concurrent syncs WITHIN a tab. Ref (not
    // state) to avoid a stale-closure read.
    if (syncingRef.current) return;
    syncingRef.current = true;
    setSyncing(true);
    try {
      await withSyncLock(async () => {
        // replica fresh first (user-scoped guard); a revoked view empties its tables (fix 87)
        await pullDelta(clinicId, userId, viewableKey === undefined ? undefined : viewableKey.split(',').filter(Boolean));
        const res = await drainQueue(); // then push local writes
        if (res.succeeded || res.conflicts || res.errors) {
          // local writes reconciled — let screens refetch the canonical data
          queryClient.invalidateQueries();
          channel?.postMessage({ type: 'reconciled' }); // B5: tell other tabs to refresh
        }
      });
      setStorageFull(false); // a clean pull means storage is no longer full (B6)
      setInitialSyncDone(true); // first full pull finished (B9)
      setLastSyncAt(await getLastSync());
      reportConnectivitySuccess(); // the pull round-tripped — we are reachable
    } catch (e) {
      // B6: a quota failure is surfaced as a banner; other errors are swallowed
      // and retried on the next online/visibility event — but a TRANSPORT
      // failure is RECORDED so isOnline reflects observed reality (a local
      // Dexie/processing error is neutral and must never fake-offline us).
      if (e instanceof SyncStorageFullError) {
        setStorageFull(true);
        // The pull ROUND-TRIPPED (quota is a storage verdict, not reachability)
        // — clear any observed-down so writes route to the server, the one
        // place a storage-full device can still save.
        reportConnectivitySuccess();
      } else {
        reportFromError(e);
      }
    } finally {
      await refreshCounts();
      setSyncing(false);
      syncingRef.current = false;
    }
  }, [clinicId, userId, viewableKey, queryClient, refreshCounts, channel]);

  // Fix 87: drop what a revoked view covered as soon as the set is known or changes, offline too. A
  // sync already running drops the call sync() makes on the same change (syncingRef), so the wipe
  // cannot wait for it; the next pull still turns a regained view into a full pull.
  useEffect(() => {
    if (viewableKey === undefined) return;
    void forgetUnviewable(viewableKey.split(',').filter(Boolean)).then(() => queryClient.invalidateQueries());
  }, [viewableKey, queryClient]);

  // B5: receive a sibling tab's "reconciled" → REFRESH ONLY (never sync(), which
  // would drain + re-post and storm the tabs). Close the channel on unmount.
  useEffect(() => {
    if (!channel) return;
    const onMsg = (e: MessageEvent) => {
      if (e.data?.type === 'reconciled') {
        void refreshCounts();
        queryClient.invalidateQueries();
      }
    };
    channel.addEventListener('message', onMsg);
    return () => {
      channel.removeEventListener('message', onMsg);
      channel.close();
    };
  }, [channel, refreshCounts, queryClient]);

  // B14 #5: emit an at-risk health heartbeat whenever a health-relevant signal
  // changes (the helper self-throttles + is inert when offline mode is off).
  useEffect(() => {
    void emitAtRiskTelemetry({
      persisted,
      queueDepth: unsyncedCount,
      conflictDepth: conflictCount,
      failedDepth: failedCount,
    });
  }, [persisted, unsyncedCount, conflictCount, failedCount]);

  const resolveReview = useCallback(
    async (opId: string, action: ReviewAction) => {
      if (action === 'keepMine') await resolveKeepMine(opId);
      else if (action === 'keepServer') await resolveKeepServer(opId);
      else if (action === 'retry') await retryOp(opId);
      else await discardOp(opId);
      queryClient.invalidateQueries();
      await sync(); // push any newly-pending op + refresh counts/reviewOps
    },
    [queryClient, sync],
  );

  // Observed-connectivity flips (engine network paths + the sync loop report
  // them) drive isOnline; a flip back to online drains the queue promptly.
  useEffect(
    () =>
      subscribeConnectivity(() => {
        const on = isEffectivelyOnline();
        setOnline(on);
        if (on) {
          void sync();
        } else {
          // Mirror the 'offline' event handler: arm Background Sync so the SW
          // can drain the queue when the BROWSER detects connectivity, even if
          // this window is hidden (the 30s probe only runs while visible).
          void requestBackgroundDrainSync();
        }
      }),
    [sync],
  );

  // Recovery probe for the virtual-adapter case: observed-down while the
  // adapter still claims online means the 'online' event will NEVER fire, so
  // nothing else would clear the down state on an idle tab. A delta pull every
  // 30s (visible tabs only) is the probe; success flips us back via sync().
  useEffect(() => {
    const id = window.setInterval(() => {
      if (navigator.onLine && !isEffectivelyOnline() && document.visibilityState === 'visible') {
        void sync();
      }
    }, 30_000);
    return () => window.clearInterval(id);
  }, [sync]);

  useEffect(() => {
    const onOnline = () => {
      // Optimistic: the adapter says we're back — clear observed-down and sync;
      // if the event lied, the pull fails and re-reports the failure.
      reportConnectivitySuccess();
      setOnline(isEffectivelyOnline());
      void sync();
    };
    const onOffline = () => {
      setOnline(false);
      void refreshCounts();
      // B9: ask the browser to wake us (Background Sync) when connectivity
      // returns, so a backgrounded/throttled PWA still drains its queue.
      void requestBackgroundDrainSync();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void sync();
        // Browsers grant persistent storage lazily (install/engagement) — re-ask
        // so the durability advisory clears as soon as the grant flips.
        void checkPersisted();
      }
    };
    const onInstalled = () => void checkPersisted();
    // B9: the SW (woken by Background Sync) posts this to drain while open.
    const onSwMessage = (e: MessageEvent) => {
      if (e.data?.type === 'DRAIN_QUEUE') void sync();
    };
    // An offline write just queued an op (executeWrite/Update/Delete) — refresh the
    // unsynced/needs-review counts NOW so the "N unsynced" badge updates immediately
    // instead of waiting for the next sync/visibility/online event.
    const onQueueChanged = () => void refreshCounts();
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    window.addEventListener('st-offline-queue-changed', onQueueChanged);
    window.addEventListener('appinstalled', onInstalled);
    document.addEventListener('visibilitychange', onVisible);
    navigator.serviceWorker?.addEventListener('message', onSwMessage);
    void checkPersisted();
    void isInitialSyncComplete().then(setInitialSyncDone); // B9: seed first-run state
    // B12: seed the last-sync time from the replica so a cold offline launch
    // knows how stale its data is (sync() overwrites it when online).
    void getLastSync().then((t) => {
      if (t) setLastSyncAt(t);
    });
    void sync();
    void refreshCounts();
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('st-offline-queue-changed', onQueueChanged);
      window.removeEventListener('appinstalled', onInstalled);
      document.removeEventListener('visibilitychange', onVisible);
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
    };
  }, [sync, refreshCounts, checkPersisted]);

  return (
    <Ctx.Provider
      value={{
        isOnline,
        syncing,
        lastSyncAt,
        unsyncedCount,
        conflictCount,
        failedCount,
        reviewOps: reviewList,
        persisted,
        storageFull,
        initialSyncDone,
        resolveReview,
        refresh: () => void sync(),
      }}
    >
      {children}
    </Ctx.Provider>
  );
};
